import { useQuery } from '@apollo/client';
import { FileText, History, Users } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import { StatusBadge } from '@/components/ui/status-badge';
import { StatusBadgeDisplay } from '@/components/ui/status-badge-display';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';

import { USER_QUERY } from './users.gql.tsx';

interface UserDetailsProps {
	userId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

export const UserEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Users className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const UserDetailsHeader = ({
	user,
	className,
	...actions
}: {
	user: Pick<
		UserRecord,
		'userId' | 'isActive' | 'email' | 'firstName' | 'lastName' | 'username'
	>;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t } = useTranslation('common');
	const { t: tU } = useTranslation('users');
	const fullName = formatFullName(
		user.firstName,
		user.lastName,
		user.username,
		tU
	);
	const email = user.email?.trim();
	const entityTypeName = user.isActive ? t('activeUser') : t('inactiveUser');

	return (
		<EntityDetailsHeader
			icon={<UserEntityIcon />}
			name={fullName}
			statusBadge={<StatusBadge isActive={user.isActive} />}
			entityTypeName={entityTypeName}
			subtitle={
				email ? (
					<a href={`mailto:${email}`} className="hover:underline break-all">
						{email}
					</a>
				) : undefined
			}
			entityId={actions.entityId ?? user.userId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

interface RoleView {
	roleId: number;
	name: string;
	description?: string | null;
}

interface UserRoleView {
	roleId: number;
	role?: RoleView | null;
}

interface UserPreferenceView {
	userPreferenceId: string;
	settingsJson: string;
}

interface FacilityView {
	facilityId: string;
	name?: string | null;
}

interface UserRecord {
	userId: string;
	username?: string | null;
	email?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	isActive: boolean;
	createdAt: string;
	userRoles?: UserRoleView[] | null;
	facilities?: FacilityView[] | null;
	userPreferences?: UserPreferenceView[] | null;
}

interface UserQueryResponse {
	user?: UserRecord | null;
}

const formatFullName = (
	firstName?: string | null,
	lastName?: string | null,
	username?: string | null,
	t?: (key: string) => string
) => {
	const parts = [firstName, lastName].filter(Boolean);
	if (parts.length === 0) {
		return username || (t ? t('details.notAvailable') : 'N/A');
	}

	return parts.join(' ');
};

const formatSettings = (settingsJson: string) => {
	try {
		const parsed = JSON.parse(settingsJson);
		return JSON.stringify(parsed, null, 2);
	} catch {
		return settingsJson;
	}
};

export const UserDetails = ({
	userId,
	activeTab,
	onTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: UserDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tU } = useTranslation('users');
	const { data, loading } = useQuery<UserQueryResponse>(USER_QUERY, {
		variables: { id: userId }
	});
	const { shouldShowLoading } = useDelayedLoading(loading, {
		delay: 150,
		showInitialLoading: true
	});

	const user = useMemo<UserRecord | null>(() => {
		return data?.user ?? null;
	}, [data?.user]);

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="h-5 rounded w-28 animate-pulse bg-muted" />
				<div className="h-5 rounded w-36 animate-pulse bg-muted" />
				<div className="w-full h-5 rounded animate-pulse bg-muted" />
			</div>
		);
	}

	if (!user) {
		return (
			<div className="py-16 text-base text-center text-muted-foreground">
				{tU('details.unavailable')}
			</div>
		);
	}

	const fullName = formatFullName(
		user.firstName,
		user.lastName,
		user.username,
		tU
	);
	const roles =
		user.userRoles?.map(roleMapping => {
			const role =
				roleMapping.role ??
				(roleMapping.roleId != null
					? {
							roleId: roleMapping.roleId,
							name: `${tU('details.rolePrefix')}${roleMapping.roleId}`
						}
					: null);
			return role;
		}) ?? [];
	const preferences = user.userPreferences ?? [];

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<UserDetailsHeader
					user={user}
					entityId={userId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

		<Tabs
			defaultValue={activeTab !== undefined ? undefined : 'details'}
			value={activeTab}
			onValueChange={onTabChange}
			className="w-full"
		>
			{!hideTabsList && (
				<TabsList>
					<TabLink value="details" icon={FileText} label={t('tabs.details')} />
					<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
				</TabsList>
			)}

			<TabsContent value="details" className="space-y-6 text-base mt-6">
				<DetailSection>
					<DetailFieldsGrid>
						<DetailField
							label={tU('details.fullName')}
							value={fullName}
						/>
						<DetailField
							label={tU('details.email')}
							value={user.email ?? tU('details.notAvailable')}
						/>
						<DetailField
							label={tU('details.username')}
							value={user.username ?? tU('details.notAvailable')}
						/>
						<DetailField
							label={tU('details.status')}
							value={
								<StatusBadgeDisplay
									isActive={user.isActive}
									activeLabel={tU('details.active')}
									inactiveLabel={tU('details.inactive')}
								/>
							}
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.roles_permissions')}>
					<DetailFieldsGrid>
						{roles.length > 0 ? (
							<div className="flex flex-wrap gap-2">
								{roles.map(role => {
									if (!role) {
										return null;
									}

									return (
										<Badge
											key={role.roleId}
											variant="secondary"
										>
											{role.name}
										</Badge>
									);
								})}
							</div>
						) : (
							<p className="text-base text-muted-foreground">
								{tU('details.noRoles')}
							</p>
						)}
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={tU('details.facilitiesAccess')}>
					<DetailFieldsGrid>
						{(user.facilities ?? []).length > 0 ? (
							<div className="flex flex-wrap gap-2">
								{user.facilities?.map(facility => (
									<Badge
										key={facility.facilityId}
										variant="outline"
									>
										{facility.name ?? tU('details.unnamedFacility')}
									</Badge>
								))}
							</div>
						) : (
							<p className="text-base text-muted-foreground">
								{tU('details.noFacilities')}
							</p>
						)}
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.preferences')}>
					<DetailFieldsGrid>
						{preferences.length > 0 ? (
							<div className="space-y-3">
								{preferences.map(preference => (
									<div
										key={preference.userPreferenceId}
										className="p-3 border rounded border-muted"
									>
										<p className="text-base font-semibold tracking-wide uppercase text-muted-foreground">
											{tU('details.preferencePrefix')}#
											{preference.userPreferenceId}
										</p>
										<pre className="mt-2 text-base leading-relaxed whitespace-pre-wrap text-muted-foreground">
											{formatSettings(
												preference.settingsJson
											)}
										</pre>
									</div>
								))}
							</div>
						) : (
							<p className="text-base text-muted-foreground">
								{tU('details.noPreferences')}
							</p>
						)}
					</DetailFieldsGrid>
				</DetailSection>
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab
					data={{
						createdAt: user.createdAt
					}}
				/>
			</TabsContent>
		</Tabs>
		</div>
	);
};
